import { DistributorDataTypeV2 } from 'elements/hardwareSelectorV2/types'
import { StockLevelTypeV2 } from 'opensolar-common'
import { DistributorInfo, ProcessedWarehouse } from './types'

/**
 * Extract and aggregate warehouse locations from distributor data
 */
export const extractWarehouseLocations = (
  data: DistributorDataTypeV2[],
  selectedDistributor?: string
): ProcessedWarehouse[] => {
  const locationMap = new Map<string, ProcessedWarehouse>()

  data.forEach((order: DistributorDataTypeV2) => {
    if (selectedDistributor && order.distributor !== selectedDistributor) {
      return
    }

    order.stock_levels?.forEach((stock: StockLevelTypeV2) => {
      if (stock.location?.key) {
        const key = stock.location.key

        if (!locationMap.has(key)) {
          locationMap.set(key, {
            key,
            name: stock.location.name,
            address: stock.location.address,
            totalStock: 0,
            inStock: false,
            suppliers: [],
          })
        }

        const loc = locationMap.get(key)!
        loc.totalStock += stock.quantity
        loc.inStock = loc.inStock || stock.in_stock

        if (!loc.suppliers.includes(order.distributor_name)) {
          loc.suppliers.push(order.distributor_name)
        }
      }
    })
  })

  return Array.from(locationMap.values())
}

/**
 * Get unique distributors from the data
 */
export const getDistributors = (data: DistributorDataTypeV2[]): DistributorInfo[] => {
  const distributorMap = new Map<string, { name: string; count: number }>()

  data.forEach((order: DistributorDataTypeV2) => {
    if (!distributorMap.has(order.distributor)) {
      distributorMap.set(order.distributor, {
        name: order.distributor_name,
        count: 0,
      })
    }
    distributorMap.get(order.distributor)!.count++
  })

  return Array.from(distributorMap.entries()).map(([key, value]) => ({
    key,
    name: value.name,
    count: value.count,
  }))
}

/**
 * Check if the selected distributor only has global warehouse (all stock levels have location: null)
 * This means the supplier delivers from a single warehouse and hasn't set up multiple locations
 */
export const hasOnlyGlobalWarehouse = (data: DistributorDataTypeV2[], selectedDistributor?: string): boolean => {
  const stockLevels = data
    .filter((order) => !selectedDistributor || order.distributor === selectedDistributor)
    .flatMap((order) => order.stock_levels ?? [])

  if (stockLevels.length === 0) return false

  return !stockLevels.some((stock) => stock.location?.key)
}

/**
 * Calculate total national stock for a distributor (sum of all stock levels without location)
 */
export const getNationalStock = (data: DistributorDataTypeV2[], selectedDistributor?: string): number => {
  return data
    .filter((order) => !selectedDistributor || order.distributor === selectedDistributor)
    .flatMap((order) => order.stock_levels ?? [])
    .filter((stock) => !stock.location?.key)
    .reduce((total, stock) => total + (stock.quantity || 0), 0)
}

/**
 * Calculate distance between two points using Haversine formula
 * @returns distance in kilometers
 */
export const calculateDistanceKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371 // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Convert kilometers to miles
 */
export const kmToMiles = (km: number): number => km * 0.621371

/**
 * Format distance in miles
 */
export const formatDistanceMiles = (km: number): string => `${kmToMiles(km).toFixed(1)}m`
